import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Plus, X,
  Zap, Edit2, Trash2, Check,
  ScanLine, TestTube2, ArrowRight, Layers, BookOpen, ChevronRight, SlidersHorizontal,
} from 'lucide-react';
import { POPULAR_STOCKS } from './strategy/strategyConstants';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import OrderModal from '../components/OrderModal';
import { formatPrice } from '../utils/formatters';
import { useWatchlist } from '../hooks/useWatchlist';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';


function TradePage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { prices: socketPrices } = useSocket();

  // Order Modal State
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [orderType, setOrderType] = useState('BUY');
  const [orderInitialValues, setOrderInitialValues] = useState(null);

  // Active watchlist tab
  const [activeWatchlistId, setActiveWatchlistId] = useState(null);

  // Stock Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [stocks, setStocks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  const {
    watchlists, setWatchlists, loading: watchlistsLoading,
    fetchWatchlists, addToWatchlist, removeFromWatchlist,
    createWatchlist, renameWatchlist, deleteWatchlist, reorderWatchlists,
  } = useWatchlist();
  const [showAddToWatchlist, setShowAddToWatchlist] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [showAddWatchlistInput, setShowAddWatchlistInput] = useState(false);
  const [watchlistError, setWatchlistError] = useState('');
  // Rename state
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  // Drag-to-reorder state
  const [dragIndex, setDragIndex] = useState(null);
  
  // Real strategies from Strategy Lab API
  const [liveStrategies, setLiveStrategies] = useState([]);
  const [liveStrategiesLoading, setLiveStrategiesLoading] = useState(true);
  const [scannerCount, setScannerCount] = useState(null);
  const [foPositionCount, setFoPositionCount] = useState(null);

  useEffect(() => {
    fetchStocks();
    fetchLiveStrategies();
    fetchWatchlists().then(data => {
      if (data.length > 0) setActiveWatchlistId(prev => prev || data[0].id);
    });
    axios.get(`${API_URL}/scanners/results`, { params: { limit: 100, timeframe: 'swing' } })
      .then(r => setScannerCount((r.data || []).length)).catch(() => {});
    axios.get(`${API_URL}/fo/positions`)
      .then(r => setFoPositionCount((r.data || []).length))
      .catch(() => {}); // 403 for non-premium users — badge just won't show
  }, []);

  // Auto-open order modal when navigated from Position Size Calculator
  useEffect(() => {
    const s = location.state;
    if (!s?.symbol || !s?.entry) return;
    axios.get(`${API_URL}/stocks/${s.symbol}`)
      .then(r => {
        setSelectedStock({ ...r.data, current_price: s.entry });
        setOrderType('BUY');
        setOrderInitialValues({ tradeReason: 'technical' });
        setOrderModalOpen(true);
      })
      .catch(() => {
        // Stock not found — open modal with minimal info
        setSelectedStock({ symbol: s.symbol, current_price: s.entry });
        setOrderType('BUY');
        setOrderInitialValues({ tradeReason: 'technical' });
        setOrderModalOpen(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLiveStrategies = async () => {
    try {
      const res = await axios.get(`${API_URL}/algo-flows/strategies`);
      setLiveStrategies(res.data || []);
    } catch (e) {
      // non-critical widget — stays empty on failure
    } finally {
      setLiveStrategiesLoading(false);
    }
  };

  // Sync live prices from Socket.io broadcast (replaces per-symbol polling)
  useEffect(() => {
    if (!socketPrices || !Object.keys(socketPrices).length) return;
    const allSymbols = new Set(watchlists.flatMap(wl => wl.stocks || []));
    const relevant = {};
    for (const sym of allSymbols) {
      if (socketPrices[sym]?.price) relevant[sym] = socketPrices[sym].price;
    }
    if (Object.keys(relevant).length) setLivePrices(prev => ({ ...prev, ...relevant }));
  }, [socketPrices, watchlists]);

  const fetchStocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/stocks`);
      setStocks(response.data);
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  useEffect(() => {
    if (searchTerm.length > 0) {
      const filtered = stocks.filter(s => 
        s.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.company_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(filtered.slice(0, 6));
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, stocks]);

  // Top 4 popular stocks for the watchlist empty-state quick-add chips
  const quickAddStocks = POPULAR_STOCKS.slice(0, 4).map(sym => {
    const found = stocks.find(s => s.symbol === sym);
    return found || { symbol: sym, company_name: sym.replace('.NS', '') };
  });

  // Open order modal — merge in latest live price so modal shows correct price
  const openOrderModal = (stock, type) => {
    const lp = livePrices[stock.symbol];
    setSelectedStock(lp ? { ...stock, current_price: lp } : stock);
    setOrderType(type);
    setOrderInitialValues(null);
    setOrderModalOpen(true);
  };

  const handleWlDragStart = (idx) => setDragIndex(idx);
  const handleWlDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    reorderWatchlists(dragIndex, idx);
    setDragIndex(idx);
  };
  const handleWlDragEnd = () => setDragIndex(null);

  const addNewWatchlist = async () => {
    const trimmed = newWatchlistName.trim();
    if (!trimmed) return;
    const duplicate = watchlists.some(wl => wl.name.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) {
      setWatchlistError(`"${trimmed}" already exists`);
      return;
    }
    setWatchlistError('');
    const newWl = await createWatchlist(trimmed);
    if (newWl) {
      setActiveWatchlistId(newWl.id);
      setNewWatchlistName('');
      setShowAddWatchlistInput(false);
    }
  };

  const startRename = (wl) => {
    setRenamingId(wl.id);
    setRenameValue(wl.name);
  };

  const submitRename = async (watchlistId) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    const duplicate = watchlists.some(wl => wl.id !== watchlistId && wl.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      setWatchlistError(`"${name}" already exists`);
      return;
    }
    setWatchlistError('');
    await renameWatchlist(watchlistId, name);
    setRenamingId(null);
  };

  const handleDeleteWatchlist = async (watchlistId) => {
    const ok = await deleteWatchlist(watchlistId);
    if (ok && activeWatchlistId === watchlistId) {
      const remaining = watchlists.filter(wl => wl.id !== watchlistId);
      setActiveWatchlistId(remaining.length > 0 ? remaining[0].id : null);
    }
  };

  return (
    <div className="space-y-2 pb-4" data-testid="trade-page">

      {/* ── Top hero cards: Scanners + Screener + Backtest ── */}
      <div className="grid grid-cols-2 gap-2">

        {/* Scanners card */}
        <button
          onClick={() => navigate('/scanners')}
          className="flex flex-col justify-between p-3 rounded-2xl text-left transition-all"
          style={{
            background: `linear-gradient(135deg, #8b5cf615 0%, #6366F110 100%)`,
            border: `1px solid #8b5cf630`,
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#8b5cf660'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#8b5cf630'}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#8b5cf620' }}>
              <ScanLine className="w-3.5 h-3.5" style={{ color: '#8b5cf6' }} />
            </div>
            <p className="text-[13px] font-bold leading-tight" style={{ color: colors.primaryText }}>Scan the Market</p>
          </div>
          <p className="text-[11px] mb-2" style={{ color: colors.secondaryText }}>
            {scannerCount != null ? `${scannerCount} signals today` : 'Find your next trade'}
          </p>
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-semibold" style={{ color: '#8b5cf6' }}>Run Scanners</span>
            <ArrowRight className="w-3 h-3" style={{ color: '#8b5cf6' }} />
          </div>
        </button>

        {/* Screener card */}
        <button
          onClick={() => navigate('/screener')}
          className="flex flex-col justify-between p-3 rounded-2xl text-left transition-all"
          style={{
            background: `linear-gradient(135deg, #06b6d415 0%, #0ea5e910 100%)`,
            border: `1px solid #06b6d430`,
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#06b6d460'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#06b6d430'}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#06b6d420' }}>
              <SlidersHorizontal className="w-3.5 h-3.5" style={{ color: '#06b6d4' }} />
            </div>
            <p className="text-[13px] font-bold leading-tight" style={{ color: colors.primaryText }}>Screener</p>
          </div>
          <p className="text-[11px] mb-2" style={{ color: colors.secondaryText }}>Filter by sector, signal & pattern</p>
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-semibold" style={{ color: '#06b6d4' }}>Open Screener</span>
            <ArrowRight className="w-3 h-3" style={{ color: '#06b6d4' }} />
          </div>
        </button>

        {/* Backtest card — full width below */}
        <button
          onClick={() => navigate('/backtest')}
          className="col-span-2 flex items-center justify-between p-3.5 rounded-2xl text-left transition-all"
          style={{
            background: `linear-gradient(135deg, #6366F115 0%, #06b6d410 100%)`,
            border: `1px solid #6366F130`,
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#6366F160'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#6366F130'}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#6366F120' }}>
              <TestTube2 className="w-4.5 h-4.5" style={{ color: '#6366F1' }} />
            </div>
            <div>
              <p className="text-[13px] font-bold leading-tight" style={{ color: colors.primaryText }}>Backtest Lab</p>
              <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>Validate your strategy before you trade</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[11px] font-semibold" style={{ color: '#6366F1' }}>Open Lab</span>
            <ArrowRight className="w-3 h-3" style={{ color: '#6366F1' }} />
          </div>
        </button>

      </div>

      {/* ── F&O hero card ── */}
      <button
        onClick={() => navigate('/derivatives')}
        className="w-full flex flex-col justify-between p-3.5 rounded-2xl text-left transition-all"
        style={{
          background: `linear-gradient(135deg, #06b6d415 0%, #8b5cf610 100%)`,
          border: `1px solid #06b6d430`,
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#06b6d460'}
        onMouseLeave={e => e.currentTarget.style.borderColor = '#06b6d430'}
      >
        {/* Icon + title row */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#06b6d420' }}>
            <Layers className="w-4 h-4" style={{ color: '#06b6d4' }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>F&amp;O Trading</p>
              {foPositionCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ backgroundColor: '#16C78420', color: '#16C784' }}>
                  {foPositionCount} open
                </span>
              )}
            </div>
            <p className="text-[10.5px]" style={{ color: colors.secondaryText }}>NIFTY &amp; BANKNIFTY futures and options</p>
          </div>
        </div>
        {/* Tagline */}
        <p className="text-[16px] font-semibold mb-1" style={{ color: colors.primaryText }}>Higher the profit, higher the risk</p>
        {/* Tip */}
        <p className="text-[11px] mb-3" style={{ color: colors.secondaryText }}>
          Always enter with a plan
        </p>
        {/* CTA */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] font-semibold" style={{ color: '#06b6d4' }}>Place Order</span>
          <ArrowRight className="w-3 h-3" style={{ color: '#06b6d4' }} />
        </div>
      </button>

      {/* Pre-trade playbook nudge */}
      <button
        onClick={() => navigate('/trade-walkthrough')}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
        style={{ background: 'linear-gradient(135deg, #06b6d412 0%, #6366f108 100%)', border: '1px solid #06b6d428' }}>
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#06b6d4' }} />
          <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Blank screen syndrome? <span style={{ color: '#06b6d4' }}>Open the Pre-Trade Playbook</span>
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#06b6d450' }} />
      </button>

      {/* ── Trade Equity card (search) ── */}
      <div className="rounded-2xl overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #06b6d412 0%, ${colors.cardBg} 60%)`,
          border: `1px solid #06b6d430`,
        }}>

        {/* Header row */}
        <div className="flex items-center gap-2.5 px-3.5 py-3"
          style={{ borderBottom: `1px solid #06b6d420` }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#06b6d420' }}>
            <Search className="w-4 h-4" style={{ color: '#06b6d4' }} />
          </div>
          <div className="leading-tight">
            <p className="font-bold text-[14px]" style={{ color: colors.primaryText }}>Trade Equity</p>
            <p className="text-[10.5px]" style={{ color: colors.secondaryText }}>Search &amp; buy NSE stocks</p>
          </div>
        </div>

        {/* Search input */}
        <div className="flex items-center gap-2 px-3.5 py-2.5" style={{ borderBottom: searchResults.length > 0 ? `1px solid #06b6d415` : 'none' }}>
          <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#06b6d480' }} />
          <input
            type="text"
            placeholder="Search and add stocks…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent text-[13px] outline-none"
            style={{ color: colors.primaryText }}
            data-testid="stock-search-input"
            data-tour="stock-search"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="flex-shrink-0">
              <X className="w-3 h-3" style={{ color: colors.secondaryText }} />
            </button>
          )}
        </div>

        {/* Search results */}
        {searchResults.length > 0 && (
          <div>
            {searchResults.map((stock, idx) => (
              <div key={stock.symbol} className="flex items-center justify-between px-3.5 py-2"
                style={{ borderBottom: `1px solid #06b6d410` }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#06b6d408'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="flex-1 min-w-0 cursor-pointer mr-2" onClick={() => navigate(`/stock/${stock.symbol.replace('.NS', '')}`)}>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-[14px]" style={{ color: colors.primaryText }}>{stock.symbol.replace('.NS', '')}</span>
                    <span className="text-[12px] font-medium" style={{ color: stock.change_pct >= 0 ? colors.profit : colors.loss }}>
                      {stock.change_pct >= 0 ? '+' : ''}{stock.change_pct?.toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-[12px] truncate" style={{ color: colors.secondaryText }}>{stock.company_name}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-[13px] font-medium" style={{ color: colors.primaryText }}>{formatPrice(livePrices[stock.symbol] ?? stock.current_price)}</span>
                  <button onClick={() => openOrderModal(stock, 'BUY')}
                    className="px-1.5 py-1 rounded text-[12px] font-bold"
                    style={{ backgroundColor: `${colors.profit}22`, color: colors.profit }}
                    data-testid={`buy-btn-${stock.symbol}`}
                    {...(idx === 0 ? { 'data-tour': 'search-result-buy' } : {})}>B</button>
                  <button onClick={() => openOrderModal(stock, 'SELL')}
                    className="px-1.5 py-1 rounded text-[12px] font-bold"
                    style={{ backgroundColor: `${colors.loss}22`, color: colors.loss }}
                    data-testid={`sell-btn-${stock.symbol}`}>S</button>
                  <div className="relative">
                    <button onClick={() => setShowAddToWatchlist(showAddToWatchlist === stock.symbol ? null : stock.symbol)}
                      className="p-1.5 rounded-lg" style={{ backgroundColor: '#06b6d420' }}>
                      <Plus className="w-3 h-3" style={{ color: '#06b6d4' }} />
                    </button>
                    {showAddToWatchlist === stock.symbol && (
                      <div className="absolute right-0 top-full mt-1 w-44 max-w-[80vw] rounded-xl shadow-lg z-10 py-1"
                        style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
                        {watchlists.length === 0 ? (
                          <div className="p-2 text-[13px] text-center" style={{ color: colors.secondaryText }}>
                            <p className="mb-1.5">No watchlists yet</p>
                            <button onClick={() => { setShowAddWatchlistInput(true); setShowAddToWatchlist(null); }}
                              className="text-[12px] px-2 py-0.5 rounded" style={{ backgroundColor: '#06b6d4', color: '#fff' }}>
                              Create Watchlist
                            </button>
                          </div>
                        ) : (
                          watchlists.map(wl => (
                            <button key={wl.id} onClick={() => { addToWatchlist(wl.id, stock.symbol); setShowAddToWatchlist(null); }}
                              className="w-full text-left px-3 py-1.5 text-[13px]" style={{ color: colors.primaryText }}>
                              {wl.name}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Watchlist sub-header ── */}
        <div className="flex items-center justify-between px-3.5 py-3"
          style={{ borderTop: `1px solid #06b6d420`, borderBottom: `1px solid #06b6d420`, backgroundColor: '#06b6d408' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#06b6d420' }}>
              <Layers className="w-3.5 h-3.5" style={{ color: '#06b6d4' }} />
            </div>
            <span className="font-bold text-[13px]" style={{ color: colors.primaryText }}>Watchlist</span>
          </div>
          {/* Watchlist tabs */}
          <div className="flex items-center gap-1 overflow-x-auto flex-1 mx-3" style={{ scrollbarWidth: 'none' }}>
            {watchlists.map((wl, idx) => (
              <div key={wl.id} draggable
                onDragStart={() => handleWlDragStart(idx)}
                onDragOver={e => handleWlDragOver(e, idx)}
                onDragEnd={handleWlDragEnd}
                className="flex-shrink-0 flex items-center group"
                style={{ cursor: 'grab', opacity: dragIndex === idx ? 0.5 : 1 }}>
                {renamingId === wl.id ? (
                  <div className="flex items-center gap-0.5">
                    <input autoFocus value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') submitRename(wl.id); if (e.key === 'Escape') setRenamingId(null); }}
                      className="w-24 px-1.5 py-0.5 rounded text-[13px]"
                      style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: '#06b6d4', color: colors.primaryText }} />
                    <button onClick={() => submitRename(wl.id)} className="p-0.5" style={{ color: '#16C784' }}><Check className="w-2.5 h-2.5" /></button>
                    <button onClick={() => setRenamingId(null)} className="p-0.5" style={{ color: colors.secondaryText }}><X className="w-2.5 h-2.5" /></button>
                  </div>
                ) : (
                  <button onClick={() => setActiveWatchlistId(wl.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors"
                    style={{
                      backgroundColor: activeWatchlistId === wl.id ? '#06b6d4' : '#06b6d415',
                      color: activeWatchlistId === wl.id ? '#fff' : '#06b6d4',
                    }}
                    data-testid={`watchlist-tab-${wl.id}`}>
                    <span>{wl.name}</span>
                    <span className="text-[10px] opacity-80">{(wl.stocks || []).length}</span>
                  </button>
                )}
                {renamingId !== wl.id && (
                  <div className="hidden group-hover:flex items-center ml-0.5 gap-0.5">
                    <button onClick={() => startRename(wl)} className="p-0.5 opacity-60 hover:opacity-100"><Edit2 className="w-2.5 h-2.5" style={{ color: colors.secondaryText }} /></button>
                    <button onClick={() => handleDeleteWatchlist(wl.id)} className="p-0.5 opacity-60 hover:opacity-100"><Trash2 className="w-2.5 h-2.5 text-red-400" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {showAddWatchlistInput ? (
            <div className="flex items-center gap-1">
              <input type="text" placeholder="Name…" value={newWatchlistName}
                onChange={(e) => { setNewWatchlistName(e.target.value); setWatchlistError(''); }}
                className="w-20 px-1.5 py-0.5 rounded text-[12px]"
                style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: watchlistError ? '#ef4444' : '#06b6d430', color: colors.primaryText }}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') addNewWatchlist(); if (e.key === 'Escape') { setShowAddWatchlistInput(false); setNewWatchlistName(''); setWatchlistError(''); } }} />
              <button onClick={addNewWatchlist} className="px-1.5 py-0.5 rounded text-[12px] font-medium text-white" style={{ backgroundColor: '#06b6d4' }}>Add</button>
              <button onClick={() => { setShowAddWatchlistInput(false); setNewWatchlistName(''); setWatchlistError(''); }} className="p-0.5 rounded" style={{ backgroundColor: colors.inputBg }}>
                <X className="w-2.5 h-2.5" style={{ color: colors.secondaryText }} />
              </button>
            </div>
          ) : (
            <button onClick={() => setShowAddWatchlistInput(true)}
              className="p-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: '#06b6d420' }}
              title="New watchlist" data-testid="add-watchlist-btn">
              <Plus className="w-3 h-3" style={{ color: '#06b6d4' }} />
            </button>
          )}
        </div>

        {/* Duplicate name error */}
        {watchlistError && (
          <div className="mx-3 mt-2 px-2 py-1 rounded text-[11px] flex items-center justify-between gap-2"
            style={{ backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#f87171' }}>
            <span>{watchlistError}</span>
            <button onClick={() => setWatchlistError('')} className="opacity-70 hover:opacity-100"><X className="w-3 h-3" /></button>
          </div>
        )}

        {/* Stock rows */}
        <div data-testid="watchlist-content">
          {watchlistsLoading ? (
            <div className="flex justify-center py-4">
              <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: '#06b6d4', borderTopColor: 'transparent' }} />
            </div>
          ) : (() => {
            const wl = watchlists.find(w => w.id === activeWatchlistId) || watchlists[0];
            if (!wl) return (
              <div className="py-4 text-center">
                <p className="text-[13px] mb-2" style={{ color: colors.secondaryText }}>No watchlists yet</p>
                <button onClick={() => setShowAddWatchlistInput(true)}
                  className="text-[12px] px-3 py-1.5 rounded-lg font-semibold" style={{ backgroundColor: '#06b6d4', color: '#fff' }}>
                  Create First Watchlist
                </button>
              </div>
            );
            const symbols = wl.stocks || [];
            if (symbols.length === 0)
              return (
                <div className="py-3.5 px-3.5">
                  <p className="text-[12px] text-center mb-2.5" style={{ color: colors.secondaryText }}>No stocks yet — try one of these, or search above</p>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {quickAddStocks.map(stock => (
                      <button
                        key={stock.symbol}
                        onClick={() => addToWatchlist(wl.id, stock.symbol)}
                        className="flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[12px] font-semibold transition-colors"
                        style={{ backgroundColor: '#06b6d415', border: '1px solid #06b6d430', color: '#06b6d4' }}
                      >
                        {stock.symbol.replace('.NS', '')}
                        <Plus className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            return symbols.map((symbol) => {
              const detail = (wl.stocks_data || []).find(s => s.symbol === symbol) || {};
              const livePrice = livePrices[symbol];
              const displayPrice = livePrice || detail.current_price;
              const chgPct = detail.change_pct;
              const chgUp = chgPct >= 0;
              const stockObj = { symbol, company_name: detail.company_name || '', sector: detail.sector || '', current_price: livePrice || detail.current_price || 0 };
              return (
                <div key={symbol}
                  className="flex items-center px-3.5 py-2.5 border-b last:border-b-0"
                  style={{ borderColor: '#06b6d415' }}>
                  {/* Left: symbol + company */}
                  <div className="flex-1 min-w-0 cursor-pointer mr-2" onClick={() => navigate(`/stock/${symbol.replace('.NS', '')}`)}>
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[14px]" style={{ color: colors.primaryText }}>{symbol.replace('.NS', '')}</span>
                      {detail.sector && (
                        <span className="text-[11px] px-1 py-0.5 rounded hidden sm:inline"
                          style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>{detail.sector}</span>
                      )}
                    </div>
                    {detail.company_name && <p className="text-[12px] truncate" style={{ color: colors.secondaryText }}>{detail.company_name}</p>}
                  </div>
                  {/* Right: price + chg% + B S X */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {displayPrice ? (
                      <div className="text-right mr-1">
                        <p className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>{formatPrice(displayPrice)}</p>
                        {chgPct != null && (
                          <p className="text-[12px] font-medium" style={{ color: chgUp ? colors.profit : colors.loss }}>
                            {chgUp ? '+' : ''}{chgPct.toFixed(1)}%
                          </p>
                        )}
                      </div>
                    ) : null}
                    <button onClick={() => openOrderModal(stockObj, 'BUY')}
                      className="px-1.5 py-1 rounded text-[12px] font-bold"
                      style={{ backgroundColor: `${colors.profit}22`, color: colors.profit }}>B</button>
                    <button onClick={() => openOrderModal(stockObj, 'SELL')}
                      className="px-1.5 py-1 rounded text-[12px] font-bold"
                      style={{ backgroundColor: `${colors.loss}22`, color: colors.loss }}>S</button>
                    <button onClick={() => removeFromWatchlist(wl.id, symbol)}
                      className="p-0.5 rounded" style={{ backgroundColor: `${colors.loss}15` }}>
                      <X className="w-3 h-3" style={{ color: colors.loss }} />
                    </button>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* ── Algo Flows hero card ── */}
      {(() => {
        const liveFlows = liveStrategies.filter(s => s.status === 'ACTIVE' || s.status === 'PAUSED');
        const activeCount = liveStrategies.filter(s => s.status === 'ACTIVE').length;
        const hasFlows = !liveStrategiesLoading && liveFlows.length > 0;
        return (
          <button
            onClick={() => navigate('/algo-flows')}
            className="w-full flex flex-col justify-between p-3.5 rounded-2xl text-left transition-all"
            style={{
              background: `linear-gradient(135deg, #f59e0b15 0%, #ef444410 100%)`,
              border: `1px solid #f59e0b30`,
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b60'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#f59e0b30'}
          >
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#f59e0b20' }}>
                <Zap className="w-4 h-4" style={{ color: '#f59e0b' }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>Algo Flows</p>
                  {hasFlows && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                      style={{ backgroundColor: '#f59e0b20', color: '#f59e0b' }}>
                      {activeCount} active
                    </span>
                  )}
                </div>
                <p className="text-[10.5px]" style={{ color: colors.secondaryText }}>Automate your best strategy</p>
              </div>
            </div>
            <p className="text-[16px] font-semibold mb-1" style={{ color: colors.primaryText }}>Let your strategy trade while you sleep</p>
            <p className="text-[11px] mb-3" style={{ color: colors.secondaryText }}>
              Don't forget to backtest before you place a trade
            </p>
            <div className="flex items-center gap-1">
              <span className="text-[11px] font-semibold" style={{ color: '#f59e0b' }}>Create your strategy</span>
              <ArrowRight className="w-3 h-3" style={{ color: '#f59e0b' }} />
            </div>
          </button>
        );
      })()}

      {/* Order Modal */}
      <OrderModal
        isOpen={orderModalOpen}
        onClose={() => { setOrderModalOpen(false); setOrderInitialValues(null); }}
        stock={selectedStock}
        orderType={orderType}
        initialValues={orderInitialValues}
      />
    </div>
  );
}

export default TradePage;
